import { useState, useEffect } from 'react';
import axios from 'axios';
import { Users, ArrowRight, Check, Loader2, AlertTriangle, Merge, X, ShieldAlert, Languages } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CategoryMergeRules } from '@/components/CategoryMergeRules';
import { ContextualHelp } from '@/components/ContextualHelp';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimilarityBar = ({ value }) => (
  <div className="flex items-center gap-2 w-24">
    <div className="flex-1 bg-slate-100 rounded-full h-1.5">
      <div className={`h-1.5 rounded-full ${
        value >= 90 ? 'bg-red-500' : value >= 80 ? 'bg-amber-500' : 'bg-blue-500'
      }`} style={{ width: `${value}%` }} />
    </div>
    <span className="text-xs text-slate-600 font-mono w-8">{value}%</span>
  </div>
);

export const SupplierNormalization = ({ token }) => {
  const [sessions, setSessions] = useState([]);
  const [selectedUploadId, setSelectedUploadId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [appliedPairs, setAppliedPairs] = useState(new Set());
  const [dismissedPairs, setDismissedPairs] = useState(new Set());
  const [bulkThreshold, setBulkThreshold] = useState(88);
  const [bulkDismissing, setBulkDismissing] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [crossScriptCandidates, setCrossScriptCandidates] = useState([]);
  const [scanningCrossScript, setScanningCrossScript] = useState(false);

  useEffect(() => { fetchSessions(); }, []);

  const fetchSessions = async () => {
    try {
      const res = await axios.get(`${API}/spend/processing-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const classified = res.data.sessions.filter(s => s.status === 'classified');
      setSessions(classified);
      if (classified.length > 0) analyzeSession(classified[0].id);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const analyzeSession = async (uploadId) => {
    setSelectedUploadId(uploadId);
    setSuggestions([]);
    setCrossScriptCandidates([]);
    setAppliedPairs(new Set());
    setDismissedPairs(new Set());
    setAnalyzing(true);
    try {
      const res = await axios.get(`${API}/spend/supplier-normalization/${uploadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuggestions(res.data.suggestions || []);
    } catch (err) {
      toast.error('Failed to analyze suppliers');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleNormalize = async (suggestion) => {
    try {
      const res = await axios.post(`${API}/spend/supplier-normalize`, {
        record_ids: suggestion.variant_record_ids,
        new_supplier_name: suggestion.canonical,
        original_supplier_name: suggestion.variant
      }, { headers: { Authorization: `Bearer ${token}` } });

      setAppliedPairs(prev => new Set([...prev, `${suggestion.canonical}|${suggestion.variant}`]));
      const ruleMsg = res.data.rule_saved ? ' Rule saved for future uploads.' : '';
      toast.success(`Normalized "${suggestion.variant}" to "${suggestion.canonical}".${ruleMsg}`);
    } catch (err) {
      toast.error('Normalization failed');
    }
  };

  const handleDismiss = async (suggestion) => {
    try {
      await axios.post(`${API}/spend/supplier-no-merge`, {
        canonical: suggestion.canonical,
        variant: suggestion.variant
      }, { headers: { Authorization: `Bearer ${token}` } });

      setDismissedPairs(prev => new Set([...prev, `${suggestion.canonical}|${suggestion.variant}`]));
      toast.success(`Marked "${suggestion.variant}" and "${suggestion.canonical}" as separate suppliers`);
    } catch (err) {
      toast.error('Failed to dismiss');
    }
  };

  const scanCrossScript = async () => {
    if (!selectedUploadId) return;
    setScanningCrossScript(true);
    try {
      const res = await axios.post(`${API}/spend/supplier-cross-script-scan/${selectedUploadId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const found = res.data.candidates || [];
      setCrossScriptCandidates(found);
      if (res.data.skipped_reason === 'all_latin_script') {
        toast.info(`Scanned top ${res.data.scanned} suppliers by spend — all Latin script, no cross-script duplicates possible`);
      } else if (found.length === 0) {
        toast.info(`Scanned top ${res.data.scanned} suppliers by spend — no cross-script duplicates found`);
      } else {
        toast.success(`Found ${found.length} cross-script candidate${found.length === 1 ? '' : 's'} — review below`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cross-script scan failed');
    } finally {
      setScanningCrossScript(false);
    }
  };

  const bulkTargets = suggestions.filter(s => {
    const key = `${s.canonical}|${s.variant}`;
    return s.similarity < bulkThreshold && !appliedPairs.has(key) && !dismissedPairs.has(key);
  });

  const handleBulkDismissClick = () => {
    if (bulkTargets.length === 0) {
      toast.info(`No pending suggestions below ${bulkThreshold}% similarity`);
      return;
    }
    setBulkConfirmOpen(true);
  };

  const confirmBulkDismiss = async () => {
    const targets = bulkTargets;
    setBulkDismissing(true);
    setBulkConfirmOpen(false);
    try {
      const results = await Promise.allSettled(targets.map(s =>
        axios.post(`${API}/spend/supplier-no-merge`, {
          canonical: s.canonical,
          variant: s.variant
        }, { headers: { Authorization: `Bearer ${token}` } })
      ));
      const okKeys = [];
      let failed = 0;
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          okKeys.push(`${targets[idx].canonical}|${targets[idx].variant}`);
        } else {
          failed += 1;
        }
      });
      if (okKeys.length) {
        setDismissedPairs(prev => new Set([...prev, ...okKeys]));
      }
      if (failed > 0) {
        toast.error(`Blocked ${okKeys.length} pairs from future suggestions; ${failed} failed.`);
      } else {
        toast.success(`Blocked ${okKeys.length} pair${okKeys.length === 1 ? '' : 's'} (similarity <${bulkThreshold}%) from future merge suggestions`);
      }
    } finally {
      setBulkDismissing(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">
      <Loader2 size={32} className="animate-spin text-slate-400" />
    </div>;
  }

  if (sessions.length === 0) {
    return <div className="flex items-center justify-center h-96">
      <div className="text-center">
        <Users className="mx-auto mb-4 text-slate-300" size={48} />
        <p className="text-slate-600">No classified data. Upload and process data first.</p>
      </div>
    </div>;
  }

  const pendingSuggestions = suggestions.filter(s => {
    const pairKey = `${s.canonical}|${s.variant}`;
    return !appliedPairs.has(pairKey) && !dismissedPairs.has(pairKey);
  });
  const appliedCount = appliedPairs.size;
  const dismissedCount = dismissedPairs.size;

  return (
    <div className="space-y-6" data-testid="supplier-normalization-page">
      <ContextualHelp guideId="suppliers" />
      <div>
        <h1 className="text-3xl font-heading font-bold text-[#0F172A] mb-2">Supplier Normalization</h1>
        <p className="text-slate-600">Detect and merge similar supplier names using fuzzy matching</p>
      </div>

      {/* Category Merge Rules — cross-session persistence layer */}
      <CategoryMergeRules token={token} />

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium text-slate-700">Session:</label>
          <select data-testid="norm-session-selector" value={selectedUploadId || ''}
            onChange={(e) => analyzeSession(e.target.value)}
            className="px-3 py-2 border border-slate-200 rounded-sm text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            {sessions.map(s => (
              <option key={s.id} value={s.id}>{s.filename} ({s.total_records} records)</option>
            ))}
          </select>
        </div>
        {appliedCount > 0 && (
          <span className="text-xs text-green-600 font-medium">{appliedCount} merged</span>
        )}
        {dismissedCount > 0 && (
          <span className="text-xs text-slate-500 font-medium">{dismissedCount} dismissed</span>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={scanCrossScript}
          disabled={scanningCrossScript || analyzing}
          className="gap-1.5 border-teal-300 text-teal-700 hover:bg-teal-50 ml-auto"
          data-testid="cross-script-scan-button"
          title="LLM-assisted scan for the same supplier written in different scripts (e.g. Toyota ↔ トヨタ ↔ تويوتا). Scans the top 50 suppliers by spend. All results require your review — nothing auto-merges."
        >
          {scanningCrossScript ? <Loader2 size={14} className="animate-spin" /> : <Languages size={14} />}
          {scanningCrossScript ? 'Scanning…' : 'Find cross-script duplicates'}
        </Button>
      </div>

      {suggestions.length > 0 && !analyzing && (() => {
        const belowCount = bulkTargets.length;
        return (
          <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-sm" data-testid="bulk-dismiss-bar">
            <span className="text-sm text-slate-700">Bulk action:</span>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <span className="text-slate-600"><strong>Block</strong> from future suggestions when similarity is below</span>
              <input
                type="number"
                min={1}
                max={100}
                value={bulkThreshold}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  if (!Number.isNaN(v)) setBulkThreshold(Math.min(100, Math.max(1, v)));
                }}
                className="w-16 px-2 py-1 border border-slate-200 rounded-sm text-sm text-right focus:outline-none focus:ring-2 focus:ring-blue-500"
                data-testid="bulk-threshold-input"
              />
              <span>%</span>
            </label>
            <span className="text-xs text-slate-500" data-testid="bulk-match-count">
              {belowCount} pending pair{belowCount === 1 ? '' : 's'} match
            </span>
            <Button
              size="sm"
              onClick={handleBulkDismissClick}
              disabled={bulkDismissing || belowCount === 0}
              className="ml-auto bg-slate-700 hover:bg-slate-800 text-white gap-1.5"
              data-testid="bulk-dismiss-button"
              title="Stops these pairs from appearing as merge candidates in future runs. Does NOT merge them — they stay as separate suppliers."
            >
              {bulkDismissing ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
              {bulkDismissing ? 'Blocking…' : `Block from suggestions (${belowCount})`}
            </Button>
          </div>
        );
      })()}

      {/* Bulk-block confirmation modal */}
      {bulkConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="bulk-block-confirm">
          <div className="bg-white rounded-md shadow-2xl max-w-2xl w-full max-h-[85vh] flex flex-col">
            <div className="flex items-start gap-3 p-5 border-b border-slate-200 bg-amber-50/50">
              <ShieldAlert size={22} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-lg font-heading font-bold text-slate-900">
                  Block {bulkTargets.length} pair{bulkTargets.length === 1 ? '' : 's'} from future suggestions?
                </h3>
                <p className="text-sm text-slate-700 mt-1">
                  This action <strong>does NOT merge</strong> these suppliers. It tells DataRefinery to never propose them as merge candidates again.
                </p>
              </div>
              <button
                onClick={() => setBulkConfirmOpen(false)}
                className="text-slate-400 hover:text-slate-600 flex-shrink-0"
                data-testid="bulk-block-cancel-x"
              >
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-3 overflow-y-auto">
              <div className="text-xs space-y-1.5 text-slate-700">
                <p className="font-semibold text-slate-900">What happens after you confirm:</p>
                <ul className="list-disc pl-5 space-y-1 text-slate-600">
                  <li>The {bulkTargets.length} pair{bulkTargets.length === 1 ? '' : 's'} below {bulkThreshold}% similarity will stay as <strong>separate suppliers</strong>.</li>
                  <li>They will <strong>not</strong> reappear in this list, even if you re-analyze the session.</li>
                  <li>This persists across sessions — future uploads inherit the same blocks.</li>
                  <li>Need to undo? Ask the agent to clear <code className="px-1 bg-slate-100 rounded">supplier_no_merge</code>; we keep a backup.</li>
                </ul>
              </div>

              <div className="border border-slate-200 rounded-sm overflow-hidden">
                <div className="bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-700 border-b border-slate-200">
                  Sample pairs that will be blocked (showing {Math.min(8, bulkTargets.length)} of {bulkTargets.length})
                </div>
                <div className="divide-y divide-slate-100" data-testid="bulk-block-preview-list">
                  {bulkTargets.slice(0, 8).map((s, idx) => (
                    <div key={`${s.canonical}|${s.variant}|${idx}`} className="flex items-center gap-3 px-3 py-2 text-xs">
                      <span className="text-slate-700 truncate flex-1" title={s.variant}>{s.variant}</span>
                      <X size={12} className="text-rose-500 flex-shrink-0" />
                      <span className="text-slate-700 truncate flex-1" title={s.canonical}>{s.canonical}</span>
                      <span className="text-slate-400 font-mono w-12 text-right flex-shrink-0">{s.similarity}%</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-800">
                <AlertTriangle size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <span>
                  Tip: if any of these pairs look like the same company (typos / casing / abbreviations), <strong>cancel</strong> and merge them individually first. Once blocked, they won't be suggested again.
                </span>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 p-4 border-t border-slate-200 bg-slate-50">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkConfirmOpen(false)}
                data-testid="bulk-block-cancel"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={confirmBulkDismiss}
                disabled={bulkDismissing}
                className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5"
                data-testid="bulk-block-confirm-button"
              >
                {bulkDismissing ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
                Yes, block {bulkTargets.length} pair{bulkTargets.length === 1 ? '' : 's'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cross-script candidates — LLM-discovered same-entity pairs across scripts.
          Renders independently of the fuzzy/semantic list: the whole point is
          catching pairs the string matchers cannot see. */}
      {crossScriptCandidates.length > 0 && (() => {
        const existingPairKeys = new Set(
          suggestions.map(g => [g.canonical, g.variant].sort().join('|'))
        );
        const visibleCandidates = crossScriptCandidates.filter(
          s => !existingPairKeys.has([s.canonical, s.variant].sort().join('|'))
        );
        const pendingCross = visibleCandidates.filter(s => {
          const k = `${s.canonical}|${s.variant}`;
          return !appliedPairs.has(k) && !dismissedPairs.has(k);
        });
        if (visibleCandidates.length === 0) return null;
        return (
          <div className="space-y-3" data-testid="cross-script-section">
            <div className="flex items-start gap-3 p-4 bg-teal-50 border border-teal-200 rounded-sm" data-testid="cross-script-banner">
              <Languages size={20} className="text-teal-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-teal-800">
                  {pendingCross.length} cross-script candidate{pendingCross.length === 1 ? '' : 's'} — same supplier, different scripts
                </p>
                <p className="text-xs text-teal-700 mt-1">
                  Discovered by AI across scripts (Latin / Arabic / CJK / Cyrillic…). Nothing merges until you confirm.
                </p>
              </div>
            </div>
            {visibleCandidates.map((s, i) => {
              const pairKey = `${s.canonical}|${s.variant}`;
              const isApplied = appliedPairs.has(pairKey);
              const isDismissed = dismissedPairs.has(pairKey);
              return (
                <div key={`cross-${pairKey}`} data-testid={`cross-suggestion-${i}`}
                  className={`bg-white border rounded-sm p-4 transition-all ${
                    isApplied ? 'border-green-200 bg-green-50/30' :
                    isDismissed ? 'border-slate-100 bg-slate-50/30 opacity-60' :
                    'border-teal-200'
                  }`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-sm">{s.variant}</span>
                          <span className="text-xs text-slate-400">({s.variant_count} records, {s.variant_spend.toLocaleString()})</span>
                        </div>
                        <ArrowRight size={16} className="text-slate-400 flex-shrink-0" />
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-sm">{s.canonical}</span>
                          <span className="text-xs text-slate-400">({s.canonical_count} records, {s.canonical_spend.toLocaleString()})</span>
                        </div>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-teal-700 bg-teal-100 px-1.5 py-0.5 rounded-sm">Cross-script</span>
                      </div>
                      <div className="flex items-center gap-4 flex-wrap">
                        <SimilarityBar value={s.similarity} />
                        {s.reason && (
                          <span className="text-xs text-slate-500 italic" data-testid={`cross-reason-${i}`}>{s.reason}</span>
                        )}
                      </div>
                    </div>
                    {isApplied ? (
                      <span className="flex items-center gap-1 text-sm text-green-600 font-medium" data-testid={`cross-applied-${i}`}>
                        <Check size={16} /> Merged
                      </span>
                    ) : isDismissed ? (
                      <span className="flex items-center gap-1 text-sm text-slate-400 font-medium" data-testid={`cross-dismissed-${i}`}>
                        <X size={16} /> Not merged
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleDismiss(s)}
                          className="text-slate-600 border-slate-300 hover:bg-slate-50 gap-1"
                          data-testid={`cross-dismiss-${i}`}>
                          <X size={14} /> Do Not Merge
                        </Button>
                        <Button size="sm" onClick={() => handleNormalize(s)}
                          className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1"
                          data-testid={`cross-merge-${i}`}>
                          <Merge size={14} /> Merge
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })()}

      {analyzing ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 size={32} className="animate-spin text-blue-500 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Analyzing supplier names...</p>
          </div>
        </div>
      ) : suggestions.length === 0 ? (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Check size={48} className="text-green-400 mx-auto mb-3" />
            <p className="text-lg font-medium text-slate-700">All supplier names look clean</p>
            <p className="text-sm text-slate-500 mt-1">No similar or duplicate supplier names detected.</p>
          </div>
        </div>
      ) : (
        <>
          {pendingSuggestions.length > 0 && (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-sm" data-testid="norm-summary">
              <AlertTriangle size={20} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-semibold text-amber-800">
                  Found {pendingSuggestions.length} potential supplier name duplicates
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  Review each suggestion. Click "Merge" to normalize, or "Do Not Merge" to keep them as separate suppliers. Dismissed pairs won't be suggested again.
                </p>
              </div>
            </div>
          )}

          <div className="space-y-3" data-testid="norm-suggestions-list">
            {suggestions.map((s, i) => {
              const pairKey = `${s.canonical}|${s.variant}`;
              const isApplied = appliedPairs.has(pairKey);
              const isDismissed = dismissedPairs.has(pairKey);

              return (
                <div key={pairKey} data-testid={`norm-suggestion-${i}`}
                  className={`bg-white border rounded-sm p-4 transition-all ${
                    isApplied ? 'border-green-200 bg-green-50/30' :
                    isDismissed ? 'border-slate-100 bg-slate-50/30 opacity-60' :
                    'border-slate-200'
                  }`}>
                  <div className="flex items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-red-700 bg-red-50 px-2 py-0.5 rounded-sm">{s.variant}</span>
                          <span className="text-xs text-slate-400">({s.variant_count} records, {s.variant_spend.toLocaleString()})</span>
                        </div>
                        <ArrowRight size={16} className="text-slate-400 flex-shrink-0" />
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-sm">{s.canonical}</span>
                          <span className="text-xs text-slate-400">({s.canonical_count} records, {s.canonical_spend.toLocaleString()})</span>
                        </div>
                      </div>
                      <SimilarityBar value={s.similarity} />
                    </div>

                    {isApplied ? (
                      <span className="flex items-center gap-1 text-sm text-green-600 font-medium" data-testid={`norm-applied-${i}`}>
                        <Check size={16} /> Merged
                      </span>
                    ) : isDismissed ? (
                      <span className="flex items-center gap-1 text-sm text-slate-400 font-medium" data-testid={`norm-dismissed-${i}`}>
                        <X size={16} /> Not merged
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => handleDismiss(s)}
                          className="text-slate-600 border-slate-300 hover:bg-slate-50 gap-1"
                          data-testid={`norm-dismiss-${i}`}>
                          <X size={14} /> Do Not Merge
                        </Button>
                        <Button size="sm" onClick={() => handleNormalize(s)}
                          className="bg-[#0F172A] hover:bg-[#1e293b] text-white gap-1"
                          data-testid={`norm-merge-${i}`}>
                          <Merge size={14} /> Merge
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};
